import React, { useEffect } from 'react';
import { useAnalytics } from '../context/AnalyticsContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './Terms.css';

const BookingTerms: React.FC = () => {
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    trackPageView('/booking-terms', 'Booking Terms and Conditions');
  }, [trackPageView]);

  return (
    <div className="terms-page">
      <Header />

      <main className="terms-content">
        <div className="terms-container">
          <h1>Parents' Night Out – Terms & Conditions</h1>
          <p className="last-updated">Last Updated: January 26, 2026</p>

          <section className="terms-section">
            <p>
              These Terms and Conditions ("Terms") apply to participation in the Parents' Night Out – Summer Camp Showcase event (the "Event"), organized by Aika Software Inc. ("Aika") in collaboration with Curated for Kiddos (together, the "Organizers").
            </p>
            <p>
              By booking a ticket for the Event, the parent or legal guardian ("Parent," "you," or "your") agrees to be bound by these Terms.
            </p>
          </section>

          <section className="terms-section">
            <h2>1. Event Description</h2>
            <p>
              The Parents' Night Out – Summer Camp Showcase is a short-duration, supervised group activity and enrichment event during which multiple independent summer camp and activity providers ("Camp Providers") present sample activities and information about their programs.
            </p>
            <p>
              Each Camp Provider operates independently and is solely responsible for the activities they lead.
            </p>
          </section>

          <section className="terms-section">
            <h2>2. Eligibility</h2>
            <p>
              The Event is intended for children within the age range specified on the event listing.
            </p>
            <p>
              Parents must ensure their child meets all age, behavioral, and participation requirements.
            </p>
            <p>
              The Organizers reserve the right to deny participation if a child does not meet stated eligibility criteria.
            </p>
          </section>

          <section className="terms-section">
            <h2>3. Booking & Payment</h2>
            <p>
              All bookings must be completed through the official registration platform designated by the Organizers.
            </p>
            <p>
              Payment is due in full at the time of booking.
            </p>
            <p>
              Tickets are valid only for the specific Event date and time purchased.
            </p>
          </section>

          <section className="terms-section">
            <h2>4. Cancellation & Refund Policy</h2>
            <h3>Cancellation More Than 48 Hours Before the Event</h3>
            <p>
              Parents may cancel their booking at least 48 hours prior to the scheduled start time.
            </p>
            <p>
              A refund will be issued minus a non-refundable $5 service fee per booking.
            </p>
            <h3>Cancellation Within 48 Hours of the Event</h3>
            <p>
              No cash refunds will be issued.
            </p>
            <p>
              A credit toward a future Parents' Night Out event will be provided, minus the $5 service fee.
            </p>
            <h3>Event Credits</h3>
            <p>Credits:</p>
            <ul>
              <li>Are valid for one (1) year from the original event date</li>
              <li>Are non-transferable</li>
              <li>Have no cash value</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>5. Event Changes or Cancellation by Organizers</h2>
            <p>
              The Organizers reserve the right to:
            </p>
            <ul>
              <li>Modify event details (including schedule, activities, or participating providers)</li>
              <li>Substitute Camp Providers if necessary</li>
              <li>Cancel or reschedule the Event due to low enrollment, weather conditions, venue issues, or circumstances beyond reasonable control</li>
            </ul>
            <p>
              If the Event is canceled by the Organizers, parents will be offered either:
            </p>
            <ul>
              <li>A full refund (including service fees), or</li>
              <li>A full credit toward a future event</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>6. Drop-Off & Pick-Up</h2>
            <p>
              Parents must drop off and pick up their child at the designated times and locations communicated prior to the Event.
            </p>
            <p>
              Children may only be released to a parent or authorized adult listed during registration and may be required to show identification.
            </p>
            <p>
              Late pick-ups may result in additional fees or refusal of future participation.
            </p>
          </section>

          <section className="terms-section">
            <h2>7. Supervision & Responsibility</h2>
            <p>
              Camp Providers are responsible for supervising children during their respective activity sessions.
            </p>
            <p>
              Aika and Curated for Kiddos coordinate the Event but do not provide direct childcare services.
            </p>
            <p>
              Parents remain responsible for ensuring their child's suitability for group activities.
            </p>
            <p>
              A separate Parent Liability Waiver must be signed prior to participation.
            </p>
          </section>

          <section className="terms-section">
            <h2>8. Behavior Expectations</h2>
            <p>
              Children are expected to behave in a manner that is respectful and safe for other participants, Camp Providers, and staff.
            </p>
            <p>
              The Organizers reserve the right to remove a child from the Event, without refund, if their behavior poses a risk to themselves or others or significantly disrupts the Event.
            </p>
          </section>

          <section className="terms-section">
            <h2>9. Health & Safety</h2>
            <p>
              Parents confirm that:
            </p>
            <ul>
              <li>All relevant medical conditions, allergies, or special needs have been disclosed during registration</li>
              <li>Their child is healthy and able to participate in group activities</li>
            </ul>
            <p>
              Organizers and Camp Providers may provide basic first aid if necessary but are not medical professionals.
            </p>
            <p>
              In the event of an emergency where a parent cannot be reached, Parents authorize the Organizers to seek emergency medical care as deemed necessary.
            </p>
          </section>

          <section className="terms-section">
            <h2>10. Photography & Media</h2>
            <p>
              Photography or video may be taken during the Event for promotional or documentation purposes.
            </p>
            <p>
              Any use of a child's likeness will be governed by the separate Photo & Media Release, which parents may opt into or decline.
            </p>
          </section>

          <section className="terms-section">
            <h2>11. Marketing Communications & Consent</h2>
            <p>
              By booking the Event, you acknowledge and agree that:
            </p>
            <ul>
              <li>You may receive marketing emails and communications from participating Camp Providers, including information about their programs, camps, and enrollment opportunities</li>
              <li>You will be added to the mailing lists of:
                <ul>
                  <li>Aika</li>
                  <li>Curated for Kiddos</li>
                </ul>
              </li>
            </ul>
            <p>
              You may unsubscribe from marketing communications at any time using the links provided in those emails.
            </p>
            <p>
              Communications will relate to family programming, camps, or events and will not be sold to third parties.
            </p>
          </section>

          <section className="terms-section">
            <h2>12. Assumption of Risk</h2>
            <p>
              Participation in group activities carries inherent risks, including but not limited to minor injuries or accidents.
            </p>
            <p>
              By enrolling your child, you voluntarily assume all such risks as further outlined in the Event waiver.
            </p>
          </section>

          <section className="terms-section">
            <h2>13. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by Colorado law, Aika, Curated for Kiddos, the venue, and participating Camp Providers shall not be liable for indirect, incidental, or consequential damages arising from participation in the Event.
            </p>
            <p>
              Organizers are not responsible for lost, stolen, or damaged personal items brought to the Event.
            </p>
            <p>
              Liability is further governed by the separate Parent Liability Waiver.
            </p>
          </section>

          <section className="terms-section">
            <h2>14. Governing Law</h2>
            <p>
              These Terms are governed by and construed in accordance with the laws of the State of Colorado, without regard to conflict-of-law principles.
            </p>
          </section>

          <section className="terms-section">
            <h2>15. Acceptance of Terms</h2>
            <p>
              By completing your booking, you confirm that you have:
            </p>
            <ul>
              <li>Read and understood these Terms and Conditions</li>
              <li>Agreed to be bound by them</li>
              <li>Agreed to sign all required waivers prior to your child's participation</li>
            </ul>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BookingTerms;
